import { useEffect, useMemo, useState } from 'react';

import { SelectOption } from '@/shared/types';

import { BlockTitle, FormContent, FormElement, Button, CloseButton, FormInputWrapper } from '@/components/old';

import { getCountrySelect } from '@/api/apiService';
import { FormDataType } from '@/api/types';

import { IdConstants } from '../../../../../../../components/Forms/constants';
import InitialSelect from '../../../../../../../components/Forms/Select';
import InputDate from '../../../../../../../components/Inputs/InputDate';
import { StepsNavigationName } from '../../../../../../../config/services';

import { TransitDocumentFieldName, TransitDocumentFormDataProps } from './types';
import { maxAdditionalCountry, maxDate, minDate } from './utils';

interface Props {
  formData: TransitDocumentFormDataProps & FormDataType;
  handleInputChange: (name: string, value: unknown) => void;
  handleClearInput: (name: string) => void;
}

const Step3: React.FC<Props> = ({ formData, handleInputChange, handleClearInput }) => {
  const [optionsCountry, setOptionsCountry] = useState<SelectOption[]>([]);
  const [additionalTransitCountry, setAdditionalTransitCountry] = useState<
    Array<{ id: number; value?: SelectOption; key?: Date }>
  >([]);

  const customs = useMemo(() => formData.customs || '', [formData.customs]);
  const purpose = useMemo(() => formData.purpose || '', [formData.purpose]);

  const toCountry = useMemo(() => (formData.toCountry ? JSON.parse(formData.toCountry) : null), [formData.toCountry]);
  const fromCountry = useMemo(
    () => (formData.fromCountry ? JSON.parse(formData.fromCountry) : null),
    [formData.fromCountry],
  );
  const returnCountry = useMemo(
    () => (formData.returnCountry ? JSON.parse(formData.returnCountry) : null),
    [formData.returnCountry],
  );
  const transitCountry = useMemo(
    () => (formData.transitCountry ? JSON.parse(formData.transitCountry) : null),
    [formData.transitCountry],
  );
  const permitExpiredAt = useMemo(
    () => (formData.permitExpiredAt ? new Date(formData.permitExpiredAt) : null),
    [formData.permitExpiredAt],
  );

  const excludedIds = new Set([
    ...(transitCountry ? [transitCountry.id] : []),
    IdConstants.UKRAINE,
    ...additionalTransitCountry.map((item) => item?.value?.id).filter(Boolean),
  ]);

  const filterCountry = optionsCountry.filter((option) => !excludedIds.has(option.id));

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const filteredKeys = Object.keys(formData).filter((key) => key.startsWith('transitCountry_'));

      const newTransitCountries = filteredKeys.reduce(
        (acc, key, index) => {
          const isIdUnique = additionalTransitCountry.every((item) => item.id !== index);

          if (isIdUnique && formData[key]) {
            const jsonData = JSON.parse(formData[key]?.toString() ?? '');

            acc.push({
              id: index,
              value: jsonData,
            });
          }

          return acc;
        },
        [] as typeof additionalTransitCountry,
      );

      if (!formData.fromCountry && !formData.returnCountry) {
        const countries = await getCountrySelect();
        const UKRAINE = countries.find((country) => country.id === IdConstants.UKRAINE);

        handleInputChange(TransitDocumentFieldName.fromCountry, UKRAINE);
        handleInputChange(TransitDocumentFieldName.returnCountry, UKRAINE);
      }

      setAdditionalTransitCountry((prevCountries) => [...prevCountries, ...newTransitCountries]);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (): void => {
    const lastId = additionalTransitCountry[additionalTransitCountry.length - 1]?.id + 1 || 0;

    const newCountry = {
      id: lastId,
      key: new Date(),
    };

    setAdditionalTransitCountry((prevCountries) => [...prevCountries, newCountry]);
  };

  return (
    <>
      <BlockTitle id="step-description" paddingBottomMdExtra bgLight>
        {StepsNavigationName.directionOfTransportation}
      </BlockTitle>

      <FormContent>
        <FormInputWrapper label="Країна, куди прямує вантаж" required marginBottomNone>
          <div className="input-select input-select_max-width">
            <InitialSelect
              name={TransitDocumentFieldName.toCountry}
              options={optionsCountry.filter(({ id }) => id !== IdConstants.UKRAINE)}
              placeholder="Не вибрано"
              defaultValue={toCountry}
              onChange={(newValue, name): void => handleInputChange(name, newValue)}
              onMenuOpen={(): void => {
                if (!optionsCountry.length) {
                  getCountrySelect(setOptionsCountry);
                }
              }}
            />
          </div>
        </FormInputWrapper>

        <FormInputWrapper label="Країна, звідки прямує вантаж" required marginBottomNone>
          <input
            className="form-input__input"
            placeholder="Не вказано"
            type="text"
            name={TransitDocumentFieldName.fromCountry}
            disabled={true}
            value={fromCountry?.label}
          />
        </FormInputWrapper>

        <FormElement variant="title">Країни, через які буде здійснюватися зворотнє вивезення (ввезення)</FormElement>

        <FormInputWrapper label="Країна 1" required marginBottomNone>
          <div className="input-select input-select_max-width">
            <InitialSelect
              name={TransitDocumentFieldName.transitCountry}
              options={filterCountry}
              placeholder="Не вибрано"
              defaultValue={transitCountry}
              onChange={(newValue, name): void => handleInputChange(name, newValue)}
              onMenuOpen={(): void => {
                if (!optionsCountry.length) {
                  getCountrySelect(setOptionsCountry);
                }
              }}
            />
          </div>
        </FormInputWrapper>

        {additionalTransitCountry.map((element, index) => (
          <div className="form-input__row" key={element.key?.toString()}>
            <FormInputWrapper label={`Країна ${index + 2}`} required marginBottomNone>
              <div className="input-select input-select_max-width">
                <InitialSelect
                  name={'transitCountry_' + element.id}
                  options={filterCountry}
                  placeholder="Не вибрано"
                  defaultValue={element?.value || {}}
                  onChange={(event): void => {
                    setAdditionalTransitCountry((prevCountry) => {
                      const updatedTransitCountry = [...prevCountry];
                      updatedTransitCountry[index].value = event;
                      return updatedTransitCountry;
                    });
                    handleInputChange(`transitCountry_${element.id}`, event);
                  }}
                  onMenuOpen={(): void => {
                    if (!optionsCountry.length) {
                      getCountrySelect(setOptionsCountry);
                    }
                  }}
                />
              </div>
            </FormInputWrapper>
            <CloseButton
              isDefault={true}
              style={{ marginTop: '25px' }}
              onClick={(): void => {
                setAdditionalTransitCountry((prevCountry) => {
                  const updatedTransitCountry = prevCountry.filter((_, i) => i !== index);
                  return updatedTransitCountry;
                });
                handleClearInput(`transitCountry_${element.id}`);
              }}
            />
          </div>
        ))}

        {additionalTransitCountry.length < maxAdditionalCountry && (
          <Button id="add-mobilePhone-button" isBorder={true} isWidthMdExtra={true} onClick={handleClick}>
            Додати ще Країну
          </Button>
        )}

        <FormElement variant="title" />

        <FormInputWrapper label="Країна, у яку повертається вантаж" marginBottomNone>
          <div className="input-select input-select_max-width">
            <input
              className="form-input__input"
              placeholder="Не вказано"
              type="text"
              name={TransitDocumentFieldName.returnCountry}
              disabled={true}
              value={returnCountry?.label}
            />
          </div>
        </FormInputWrapper>

        <FormInputWrapper label="Митний пост" required marginBottomNone>
          <input
            className="form-input__input"
            placeholder="Не вказано"
            type="text"
            name={TransitDocumentFieldName.customs}
            value={customs}
            onChange={({ target: { name, value } }): void => handleInputChange(name, value)}
          />
        </FormInputWrapper>

        <InputDate
          required
          minDate={minDate}
          maxDate={maxDate}
          label="Строк вивезення зброї"
          name={TransitDocumentFieldName.permitExpiredAt}
          value={permitExpiredAt}
          onChange={(date): void => handleInputChange(TransitDocumentFieldName.permitExpiredAt, date)}
        />

        <FormInputWrapper label="Мета вивезення зброї" required marginBottomNone>
          <input
            className="form-input__input"
            placeholder="Не вказано"
            type="text"
            name={TransitDocumentFieldName.purpose}
            value={purpose}
            onChange={({ target: { name, value } }): void => handleInputChange(name, value)}
          />
        </FormInputWrapper>
      </FormContent>
    </>
  );
};
export default Step3;
